// Clipboard utility with fallback for non-secure contexts

export const copyToClipboard = async (text: string): Promise<boolean> => {
  // Try modern clipboard API first (works in HTTPS and localhost)
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      console.warn('Clipboard API failed, falling back to legacy method:', err);
    }
  }

  // Fallback for non-secure contexts (HTTP)
  return fallbackCopyToClipboard(text);
};

const fallbackCopyToClipboard = (text: string): boolean => {
  try {
    // Create a temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Make it invisible but accessible
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    textArea.style.zIndex = '-1';
    textArea.setAttribute('readonly', '');
    textArea.setAttribute('aria-hidden', 'true');
    
    // Add to DOM
    document.body.appendChild(textArea);
    
    // Focus and select the text
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, 99999); // For mobile devices
    
    // Execute copy command
    const successful = document.execCommand('copy');
    
    // Clean up
    document.body.removeChild(textArea);
    
    return successful;
  } catch (err) {
    console.error('Fallback clipboard copy failed:', err);
    return false;
  }
};

// Alternative method using prompt for browsers that don't support execCommand
export const showCopyPrompt = (text: string): void => {
  if (window.prompt) {
    const result = window.prompt('Copy this link to your clipboard (Ctrl+C or Cmd+C):', text);
    // User pressed cancel
    if (result === null) {
      console.log('User cancelled copy prompt');
    }
  } else {
    // Fallback alert for very old browsers
    alert(`Please copy this link manually:\n\n${text}`);
  }
};

// Check if clipboard functionality is available
export const isClipboardAvailable = (): boolean => {
  return !!(navigator.clipboard && window.isSecureContext) || 
         !!(document.queryCommandSupported && document.queryCommandSupported('copy'));
};
